import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  KeyImagesWithStatus,
  RadiologistStudyKeyImagesError,
  RadiologistStudyKeyImagesRequest,
  RadiologistStudyKeyImagesResponse,
  RadiologistStudyMedicalHistoryError,
  RadiologistStudyMedicalHistoryRequest,
  RadiologistStudyMedicalHistoryResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';
import { isNotEmpty } from '@/shared/util';

const KEY = {
  GET_RADIOLOGIST_STUDY_KEY_IMAGES: 'GET_RADIOLOGIST_STUDY_KEY_IMAGES',
  GET_RADIOLOGIST_STUDY_MEDICAL_HISTORY: 'GET_RADIOLOGIST_STUDY_MEDICAL_HISTORY',
} as const;

const MAX_POLLING_ATTEMPTS = 10;

const request = {
  getKeyImages: async (
    { payload: { params, studyId } }: RadiologistStudyKeyImagesRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.HI.get<RadiologistStudyKeyImagesResponse>(
      `${ENDPOINTS.HI.RADIOLOGIST.STUDY}${studyId}/ki-manifest`,
      { params: removeEmptyParams(params), signal },
    );

    if (!data?.key_images) {
      throw new Error('Invalid key images response');
    }

    // FC-1171: adding num_instances bypass workaround given the unstable response from HI KI query, same approach as Radform
    const isMatchingKeyImageLength =
      data.key_images.num_instances === Object.keys(data.key_images.imageKeys).length ||
      !data.key_images.num_instances;

    const hasNoImages = data.key_images.num_instances === 0;
    const hasProcessedImages =
      data.key_images.processed && isNotEmpty(Object.keys(data.key_images.processed));

    const isProcessingComplete =
      data.key_images.isProcessing === '0' &&
      data.key_images.isProcessed === true &&
      data.key_images.isAvailable === true &&
      (hasNoImages || hasProcessedImages) &&
      isMatchingKeyImageLength;

    const loadedCount = Object.keys(data.key_images.processed || {}).length;
    const totalCount = data.key_images.num_instances || loadedCount;

    return {
      isProcessing: !isProcessingComplete,
      loadedCount,
      response: data,
      totalCount,
    };
  },
  getMedicalHistory: async (
    { payload: { studyId } }: RadiologistStudyMedicalHistoryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.HI.get<RadiologistStudyMedicalHistoryResponse>(
      `${ENDPOINTS.HI.RADIOLOGIST.STUDY}${studyId}/medical-history`,
      { signal },
    );

    return data;
  },
};

const query = {
  useGetKeyImages: (
    { payload }: RadiologistStudyKeyImagesRequest,
    config?: Partial<UseQueryOptions<KeyImagesWithStatus, RadiologistStudyKeyImagesError>> & {
      enablePolling?: boolean;
      onPollingComplete?: () => void;
      onPollingTimeout?: () => void;
    },
  ) => {
    const { enablePolling, onPollingComplete, onPollingTimeout, ...queryConfig } = config ?? {};

    return useQuery<KeyImagesWithStatus, RadiologistStudyKeyImagesError>({
      queryFn: ({ signal }) => request.getKeyImages({ payload }, signal),
      queryKey: [KEY.GET_RADIOLOGIST_STUDY_KEY_IMAGES, payload.studyId],
      refetchInterval: enablePolling
        ? ({ state: { data, dataUpdateCount, errorUpdateCount, fetchFailureCount } }) => {
            if (data && !data.isProcessing) {
              onPollingComplete?.();

              return false;
            }

            // Use the highest counter across successes and failures to correctly count all attempts,
            // since dataUpdateCount only increments on success and would loop forever on persistent errors.
            const attemptCount = Math.max(dataUpdateCount, errorUpdateCount, fetchFailureCount);

            if (attemptCount >= MAX_POLLING_ATTEMPTS) {
              onPollingTimeout?.();

              return false;
            }

            // Exponential backoff with jitter: base delay doubles each attempt (1s, 2s, 4s...),
            // plus random 0-1s jitter to prevent thundering herd, capped at 30s max
            return Math.min(1000 * 2 ** attemptCount + Math.random() * 1000, 30000);
          }
        : false,
      ...queryConfig,
    });
  },
  useGetMedicalHistory: (
    { payload }: RadiologistStudyMedicalHistoryRequest,
    config?: Partial<
      UseQueryOptions<RadiologistStudyMedicalHistoryResponse, RadiologistStudyMedicalHistoryError>
    >,
  ) =>
    useQuery<RadiologistStudyMedicalHistoryResponse, RadiologistStudyMedicalHistoryError>({
      queryFn: ({ signal }) => request.getMedicalHistory({ payload }, signal),
      queryKey: [KEY.GET_RADIOLOGIST_STUDY_MEDICAL_HISTORY, payload.studyId],
      ...config,
    }),
};

export const radiologist = {
  KEY,
  query,
  request,
};
